import type { Dispatch } from 'react';
import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  Alert,
  Button,
  Checkbox,
  Field,
  IconButton,
  Select,
  Stack,
  Textarea,
  Tooltip,
  tokens,
} from '@hotjar/hotsauce';
import { TextEditor } from '@hotjar/feedback/components/common/TextEditor/TextEditor';
import type { Position } from '@hotjar/feedback/domain/IncomingFeedback/Position';
import { useCurrentSite } from '@hotjar/site';
import { useTranslation } from '@hotjar/translations';

type FieldWithValidation =
  | 'invitationMessage'
  | 'mainQuestion'
  | 'emailRequest'
  | 'additionalMessage'
  | 'thankyouMessage'
  | 'showLegal';

export type FormValidation = Record<FieldWithValidation, boolean>;

interface BaseStepProps {
  onReloadPreview: (section: string) => void;
  formValidation: FormValidation;
  isRtlLanguage: boolean;
}

interface InvitationProps extends BaseStepProps {
  widgetPosition: Position;
  invitationMessage: string;
  onInvitationMessageChanged: (newMessage: string) => void;
}

interface MainQuestionStepProps extends BaseStepProps {
  mainQuestion: string;
  autoScreenshot: boolean;
  showAdditionalMessage: boolean;
  additionalMessage: string;
  onMainQuestionChanged: (newQuestion: string) => void;
  onToggleAutoScreenshot: (shouldAuto: boolean) => void;
  onToggleAdditionalMessage: (shouldShow: boolean) => void;
  onAdditionalMessageChanged: (newMessage: string) => void;
}

interface EmailRequestProps extends BaseStepProps {
  showEmailRequest: boolean;
  emailRequest: string;
  onToggleEmailRequest: (shouldShow: boolean) => void;
  onEmailRequestChanged: (newRequest: string) => void;
}

type ConnectFeedback = 'always' | 'ask' | 'never';

interface ThankYouProps extends BaseStepProps {
  thankyouMessage: string;
  showLegal: boolean;
  siteHasLegalDetails: boolean;
  connectFeedback: ConnectFeedback;
  onThankyouMessageChanged: (newMessage: string) => void;
  onToggleLegal: (shouldShow: boolean) => void;
  onConnectFeedbackChanged: (connect: ConnectFeedback) => void;
}

export type AllSectionsProps = InvitationProps &
  MainQuestionStepProps &
  EmailRequestProps &
  ThankYouProps;

const StepContainer = styled(Stack)`
  width: 100%;
  padding: ${tokens.spacer.x2};
  border-radius: 4px;
  border: 1px solid ${tokens.color.ui_border_light};
  position: relative;
  transition: border 200ms linear, box-shadow 200ms linear;

  &:hover {
    border-color: ${tokens.color.ui_border};
    ${tokens.elevation(1)}
  }
`;

const StyledField = styled(Field)<{
  hasMarginBottom?: boolean;
  hasPaddingRight?: boolean;
  hasPaddingLeft?: boolean;
}>`
  width: 100%;
  ${({ hasMarginBottom, isInvalid }) =>
    hasMarginBottom && !isInvalid ? `margin-bottom: ${tokens.spacer.x3}` : ``}
  ${({ hasPaddingRight }) =>
    hasPaddingRight &&
    `& > span {
          padding-right: ${tokens.spacer.x4};
        }`}
  ${({ hasPaddingLeft }) => (hasPaddingLeft ? `padding-left: ${tokens.spacer.x3}` : ``)}
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 0;
  width: fit-content;
`;

const StyledTextarea = styled(Textarea)<{ isRtl: boolean }>`
  ${({ isRtl }) =>
    isRtl &&
    `
    direction: rtl;
    text-align: right;
  `}
`;

const StyledTextEditor = styled(TextEditor)<{ isRtl: boolean }>`
  margin-top: -${tokens.spacer.x2};
  ${({ isRtl }) =>
    isRtl &&
    `
  .ql-editor {
    direction: rtl;
    text-align: right;
  }
  `}
`;

const StyledSelectField = styled(Field)`
  max-width: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: ${tokens.spacer.x2};
`;

// this helps avoiding too many re-renders for this relatively heavy component
const useStepValue = (
  stepValue: string,
  onStepValueChange: (newValue: string) => void,
): [string, Dispatch<string>] => {
  const [inputValue, setInputValue] = useState(stepValue);

  useEffect(() => {
    setInputValue(stepValue);
  }, [stepValue]);

  useEffect(() => {
    const valueChangeTimeout = setTimeout(() => {
      if (inputValue !== undefined) onStepValueChange(inputValue);
    }, 100);
    return () => clearTimeout(valueChangeTimeout);
  }, [inputValue, onStepValueChange]);

  return [inputValue, setInputValue];
};

export const Invitation = ({
  widgetPosition,
  invitationMessage,
  formValidation,
  isRtlLanguage,
  onInvitationMessageChanged,
  onReloadPreview,
}: InvitationProps): JSX.Element | null => {
  const { t } = useTranslation('feedback');
  const [invitationMessageValue, setInvitationMessageValue] = useStepValue(
    invitationMessage,
    onInvitationMessageChanged,
  );
  const showStep = useMemo(
    () => widgetPosition !== 'middle_left' && widgetPosition !== 'middle_right',
    [widgetPosition],
  );

  const reloadInvitationPreview = () => onReloadPreview('initial');

  return showStep ? (
    <StepContainer spacing="x2" direction="column" onMouseEnter={reloadInvitationPreview}>
      <StyledField
        labelText={t('feedback-wizard.steps.invitation.label')}
        inputId="invitationMessage"
        isInvalid={!formValidation.invitationMessage}
        invalidText={t('feedback-wizard.steps.error-required')}
        isRequired
      >
        <StyledTextarea
          value={invitationMessageValue}
          onChange={({ target: { value } }) => setInvitationMessageValue(value)}
          placeholder={t('feedback-wizard.steps.invitation.placeholder')}
          id="invitationMessage"
          onFocus={reloadInvitationPreview}
          isInvalid={!formValidation.invitationMessage}
          isRtl={isRtlLanguage}
        />
      </StyledField>
    </StepContainer>
  ) : null;
};

export const MainQuestion = ({
  mainQuestion,
  autoScreenshot,
  showAdditionalMessage,
  additionalMessage,
  onMainQuestionChanged,
  onToggleAdditionalMessage,
  onToggleAutoScreenshot,
  onAdditionalMessageChanged,
  onReloadPreview,
  formValidation,
  isRtlLanguage,
}: MainQuestionStepProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const [mainQuestionValue, setMainQuestionValue] = useStepValue(
    mainQuestion,
    onMainQuestionChanged,
  );
  const toggleAutoScreenshot = () => onToggleAutoScreenshot(!autoScreenshot);
  const toggleAdditionalMessage = () => onToggleAdditionalMessage(!showAdditionalMessage);
  const reloadPreview = (name: string) => () => onReloadPreview(name);

  return (
    <StepContainer spacing="x2" direction="column" onMouseEnter={reloadPreview('emotion')}>
      <StyledField
        labelText={t('feedback-wizard.steps.main-question.label')}
        inputId="mainQuestion"
        isInvalid={!formValidation.mainQuestion}
        invalidText={t('feedback-wizard.steps.error-required')}
        hasMarginBottom
        isRequired
      >
        <StyledTextarea
          value={mainQuestionValue}
          onChange={({ target: { value } }) => setMainQuestionValue(value)}
          placeholder={t('feedback-wizard.steps.main-question.placeholder')}
          id="mainQuestion"
          onFocus={reloadPreview('emotion')}
          isInvalid={!formValidation.mainQuestion}
          isRtl={isRtlLanguage}
        />
      </StyledField>
      <StyledCheckbox
        isChecked={autoScreenshot}
        value={`${autoScreenshot}`}
        onChange={toggleAutoScreenshot}
        labelContent={t('feedback-wizard.steps.include-screenshot.label')}
      />
      <StyledCheckbox
        labelContent={t('feedback-wizard.steps.additional-message.label')}
        isChecked={showAdditionalMessage}
        value={`${showAdditionalMessage}`}
        onChange={toggleAdditionalMessage}
      />
      {showAdditionalMessage && (
        <StyledField
          inputId="additionalMessage"
          labelText=""
          isInvalid={!formValidation.additionalMessage}
          invalidText={t('feedback-wizard.steps.error-required')}
          hasPaddingLeft
        >
          <StyledTextEditor
            onMouseEnter={reloadPreview('comment')}
            onFocus={reloadPreview('comment')}
            value={additionalMessage}
            onChange={onAdditionalMessageChanged}
            id="additionalMessage"
            isInvalid={!formValidation.additionalMessage}
            data-testid="additionalMessage"
            isRtl={isRtlLanguage}
          />
        </StyledField>
      )}
    </StepContainer>
  );
};

export const EmailRequest = ({
  showEmailRequest,
  emailRequest,
  formValidation,
  onToggleEmailRequest,
  onEmailRequestChanged,
  onReloadPreview,
  isRtlLanguage,
}: EmailRequestProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const [emailRequestValue, setEmailRequestValue] = useStepValue(
    emailRequest,
    onEmailRequestChanged,
  );
  const toggleEmailRequest = () => onToggleEmailRequest(!showEmailRequest);
  const reloadEmailPreview = () => onReloadPreview('email');

  return showEmailRequest ? (
    <StepContainer spacing="x1" direction="column" onMouseEnter={reloadEmailPreview}>
      <StyledIconButton
        icon="trash"
        tooltipText={t('feedback-wizard.steps.email.disable-tooltip')}
        size="s"
        kind="ghost"
        onClick={toggleEmailRequest}
        data-testid="hideEmailRequest"
      />
      <StyledField
        labelText={t('feedback-wizard.steps.email.label')}
        inputId="emailRequest"
        helperText={t('feedback-wizard.steps.email.description')}
        isInvalid={!formValidation.emailRequest}
        invalidText={t('feedback-wizard.steps.error-required')}
        hasPaddingRight
        isRequired
      >
        <StyledTextarea
          value={emailRequestValue}
          onChange={({ target: { value } }) => setEmailRequestValue(value)}
          placeholder={t('feedback-wizard.steps.email.placeholder')}
          id="emailRequest"
          onFocus={reloadEmailPreview}
          isInvalid={!formValidation.emailRequest}
          isRtl={isRtlLanguage}
        />
      </StyledField>
    </StepContainer>
  ) : (
    <div>
      <Button kind="ghost" icon="plus" onClick={toggleEmailRequest}>
        {t('feedback-wizard.steps.email.toggle')}
      </Button>
    </div>
  );
};

export const ThankYou = ({
  thankyouMessage,
  showLegal,
  siteHasLegalDetails,
  connectFeedback,
  formValidation,
  onThankyouMessageChanged,
  onToggleLegal,
  onConnectFeedbackChanged,
  onReloadPreview,
  isRtlLanguage,
}: ThankYouProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const [thankyouMessageValue, setThankyouMessageValue] = useStepValue(
    thankyouMessage,
    onThankyouMessageChanged,
  );
  const toggleLegal = () => onToggleLegal(!showLegal);
  const reloadThankyouPreview = () => onReloadPreview('thankYou');
  const isLegalDisabled = !siteHasLegalDetails && !showLegal;
  const { data: currentSite } = useCurrentSite();

  return (
    <StepContainer spacing="x2" direction="column" onMouseEnter={reloadThankyouPreview}>
      <StyledField
        labelText={t('feedback-wizard.steps.thank-you-message.label')}
        inputId="thankYou"
        hasMarginBottom
        isInvalid={!formValidation.thankyouMessage}
        invalidText={t('feedback-wizard.steps.error-required')}
        isRequired
      >
        <StyledTextarea
          value={thankyouMessageValue}
          onChange={({ target: { value } }) => setThankyouMessageValue(value)}
          placeholder={t('feedback-wizard.steps.thank-you-message.placeholder')}
          id="thankYou"
          isInvalid={!formValidation.thankyouMessage}
          isRequired
          isRtl={isRtlLanguage}
        />
      </StyledField>
      <Tooltip text={t('feedback-wizard.steps.legal-info.tooltip')} isDisabled={!isLegalDisabled}>
        <div>
          <StyledCheckbox
            isChecked={showLegal}
            value={`${showLegal}`}
            onChange={toggleLegal}
            labelContent={t('feedback-wizard.steps.legal-info.label')}
            isDisabled={isLegalDisabled}
          />
        </div>
      </Tooltip>
      <StyledSelectField
        size="m"
        labelText={t('feedback-wizard.steps.connect-data.label')}
        inputId="connectFeedback"
      >
        <Select
          value={connectFeedback}
          onChange={({ target: { value } }) => onConnectFeedbackChanged(value as ConnectFeedback)}
          id="connectFeedback"
        >
          <option value="always">{t('feedback-wizard.steps.connect-data.always')}</option>
          <option value="ask">{t('feedback-wizard.steps.connect-data.ask')}</option>
          <option value="never">{t('feedback-wizard.steps.connect-data.never')}</option>
        </Select>
      </StyledSelectField>
      {!isLegalDisabled && !formValidation.showLegal && (
        <Alert
          kind="danger"
          actionText={t('feedback-wizard.steps.legal-info.caption')}
          actionHref={currentSite ? `/site/${currentSite.id}/settings#site-information` : undefined}
        >
          {t('feedback-wizard.steps.legal-info.alert')}
        </Alert>
      )}
    </StepContainer>
  );
};
